import React from 'react';
import { SEO } from '../components/SEO';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { CheckIcon, ArrowRightIcon, MessageCircleIcon, PenToolIcon, CodeIcon, CheckCircleIcon, ServerIcon, TestTubeIcon, RocketIcon, ShieldIcon, UsersIcon, TrendingUpIcon, FileTextIcon, ShoppingCartIcon, LayoutIcon, StarIcon } from 'lucide-react';
import { motion } from 'framer-motion';
type WebsiteType = {
  icon: React.ReactNode;
  title: string;
  scope: string;
  price: string;
  priceSuffix: string;
  details: string;
  highlight?: boolean;
};
export const WebsitesPage: React.FC = () => {
  const websiteTypes: WebsiteType[] = [{
    icon: <FileTextIcon className="h-6 w-6" data-id="element-2542" />,
    title: 'Onepager',
    scope: "1 tot 3 secties op één pagina",
    price: 'vanaf €499',
    priceSuffix: 'eenmalig, excl. BTW',
    details: 'Een strakke, conversiegerichte onepager met één duidelijke boodschap. Geschikt voor zzp en kleine dienstverleners die snel online willen staan met een professioneel visitekaartje.',
    highlight: true
  }, {
    icon: <LayoutIcon className="h-6 w-6" data-id="element-2543" />,
    title: 'Maatwerk website',
    scope: "4 of meer pagina's",
    price: 'Op aanvraag',
    priceSuffix: 'afgestemd op scope',
    details: 'Een volwaardige website met meerdere dienst-paginas, een blog of kennisbank, en ruimte voor SEO-uitbreiding. Voor bedrijven die hun complete dienstverlening online willen presenteren.'
  }, {
    icon: <ShoppingCartIcon className="h-6 w-6" data-id="element-2544" />,
    title: 'Webshop',
    scope: 'E-commerce, WooCommerce of maatwerk',
    price: 'Op aanvraag',
    priceSuffix: 'incl. betaalkoppeling',
    details: 'Een complete webshop met betalingen, voorraadbeheer en ordermails. Voor bedrijven die producten of diensten direct via de site willen verkopen.'
  }];
  const benefits = [{
    icon: <CheckCircleIcon className="h-6 w-6" data-id="element-2545" />,
    title: 'Custom design voor jouw merk',
    description: 'Volledig op maat ontworpen, met aandacht voor je doelgroep en de manier waarop jij wilt overkomen.'
  }, {
    icon: <UsersIcon className="h-6 w-6" data-id="element-2546" />,
    title: 'Specialist in zakelijke dienstverlening',
    description: 'Ervaring met websites voor financieel adviseurs, administratiekantoren, juristen en M&A specialisten.'
  }, {
    icon: <TrendingUpIcon className="h-6 w-6" data-id="element-2547" />,
    title: 'Gebruiksvriendelijk en helder',
    description: 'Intuitieve navigatie en een heldere structuur, zodat bezoekers snel vinden waar ze voor komen.'
  }, {
    icon: <ShieldIcon className="h-6 w-6" data-id="element-2548" />,
    title: 'Professionele uitstraling',
    description: 'Past bij sectoren waar vertrouwen alles is. Geen schreeuwerige opmaak, wel een verzorgde, premium look.'
  }, {
    icon: <StarIcon className="h-6 w-6" data-id="element-2549" />,
    title: 'Klaar voor SEO en groei',
    description: 'Vanaf dag één geschikt voor SEO-optimalisatie en uitbreiding wanneer je markt erom vraagt.'
  }];
  const topCases = [{
    name: 'Alex Construct',
    sector: 'Renovatie',
    image: 'https://images.unsplash.com/photo-1620626011761-996317b8d101?w=800&h=500&fit=crop'
  }, {
    name: 'Mijn Bedrijfs Waarderingen',
    sector: 'Financiële Dienstverlening',
    image: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800&h=500&fit=crop'
  }, {
    name: 'GymFit de Noord',
    sector: 'Crossfit',
    image: 'https://images.unsplash.com/photo-1517836357463-d25dfeac3438?w=800&h=500&fit=crop'
  }];
  const timelineSteps = [{
    icon: <MessageCircleIcon className="h-6 w-6" data-id="element-2550" />,
    title: 'Intake',
    description: 'We brengen je doelen, doelgroep en wensen helder in kaart via een persoonlijk gesprek.'
  }, {
    icon: <PenToolIcon className="h-6 w-6" data-id="element-2551" />,
    title: 'Ontwerp',
    description: 'We maken een eerste ontwerp dat past bij jouw organisatie en je sector.'
  }, {
    icon: <UsersIcon className="h-6 w-6" data-id="element-2552" />,
    title: 'Afstemmen',
    description: 'Je geeft feedback, wij verfijnen. Dit herhalen we tot het echt goed voelt.'
  }, {
    icon: <CodeIcon className="h-6 w-6" data-id="element-2553" />,
    title: 'Bouwen',
    description: 'Is het ontwerp akkoord? Dan bouwen we de website technisch op, snel, veilig en schaalbaar.'
  }, {
    icon: <TestTubeIcon className="h-6 w-6" data-id="element-2554" />,
    title: 'Testen en finetunen',
    description: 'Je bekijkt de website en geeft feedback. Wij passen aan tot alles klopt.'
  }, {
    icon: <RocketIcon className="h-6 w-6" data-id="element-2555" />,
    title: 'Oplevering',
    description: 'Bij oplevering geven we live uitleg over hoe je met je website en systemen werkt.'
  }, {
    icon: <ServerIcon className="h-6 w-6" data-id="element-2556" />,
    title: 'Hosting en onderhoud',
    description: 'Na oplevering zorgen wij voor hosting, onderhoud en beveiliging, op onze eigen omgeving.'
  }];
  return <div className="w-full min-h-screen bg-white text-[#111827] font-sans" data-id="element-2557">
      <SEO title="Website laten maken vanaf €499 | Noord Digital" description="Onepager vanaf €499, maatwerk en webshops op aanvraag. Professionele websites voor zakelijke dienstverleners, met hosting, e-mail en SEO onder één dak." keywords="website laten maken, onepager 499, maatwerk website, webshop laten maken, zakelijke website, professionele website, custom webdesign" canonical="https://noorddigital.nl/website-laten-maken" data-id="element-2558" />
      <Header data-id="element-2559" />

      {/* Hero */}
      <div className="w-full bg-gradient-to-br from-[#007AFF] to-[#1B2B44] py-16 md:py-20 relative overflow-hidden" data-id="element-2560">
        <div className="absolute inset-0 opacity-20" data-id="element-2561">
          <div className="w-full h-full bg-[url('https://images.unsplash.com/photo-1554224155-6726b3ff858f?ixlib=rb-4.0.3&auto=format&fit=crop&w=1470&q=80')] bg-cover bg-center" data-id="element-2562"></div>
        </div>

        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px] relative z-10" data-id="element-2563">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center" data-id="element-2564">
            <div className="lg:col-span-7" data-id="element-2565">
              <motion.h1 initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6
            }} className="font-bold text-white leading-tight mb-6 text-4xl md:text-5xl lg:text-6xl" data-id="element-2566">
                Website laten maken, vanaf €499 voor een onepager
              </motion.h1>
              <motion.p initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6,
              delay: 0.1
            }} className="text-white text-opacity-90 mb-8 text-lg leading-relaxed" data-id="element-2567">
                Van een strakke onepager tot een volwaardige maatwerk website of webshop. We bouwen voor zakelijke dienstverleners die online net zo overtuigend willen overkomen als in een persoonlijk gesprek. Inclusief hosting, e-mail en SEO als je dat wilt.
              </motion.p>
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6,
              delay: 0.2
            }} className="flex flex-wrap gap-4" data-id="element-2568">
                <motion.a href="#keuze" whileHover={{
                scale: 1.05
              }} whileTap={{
                scale: 0.95
              }} className="inline-flex items-center px-6 py-3 bg-white text-[#007AFF] font-semibold rounded-lg shadow-lg group" data-id="element-2569">
                  Bekijk de opties
                  <ArrowRightIcon className="h-5 w-5 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-2570" />
                </motion.a>
              </motion.div>
            </div>

            <div className="lg:col-span-5" data-id="element-2571">
              <motion.div initial={{
              opacity: 0,
              scale: 0.9
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              duration: 0.5,
              delay: 0.3
            }} className="bg-white rounded-2xl p-8 shadow-2xl relative" data-id="element-2572">
                <motion.div animate={{
                y: [0, -5, 0]
              }} transition={{
                repeat: Infinity,
                duration: 4,
                ease: 'easeInOut'
              }} className="absolute -top-3 -right-3 w-16 h-16 bg-gradient-to-br from-[#007AFF] to-[#005FCC] rounded-full flex items-center justify-center shadow-lg border-4 border-white" data-id="element-2573">
                  <img src="https://cdn.magicpatterns.com/uploads/fv5nFyNt1SPvWUkxd2YKYJ/Norden_portret_300dpi_2.png" alt="Norden mascotte begeleidt je website" className="w-full h-full object-cover rounded-full" data-id="element-2574" />
                </motion.div>
                <div className="text-center mb-6" data-id="element-2575">
                  <p className="text-xs uppercase tracking-wide text-[#6B7280] mb-2" data-id="element-2576">
                    Onepager vanaf
                  </p>
                  <div className="flex items-baseline justify-center gap-2 mb-2" data-id="element-2577">
                    <span className="text-6xl md:text-7xl font-bold text-[#111827]" data-id="element-2578">
                      €499
                    </span>
                  </div>
                  <p className="text-[#6B7280] text-sm" data-id="element-2579">eenmalig, excl. BTW</p>
                </div>

                <div className="space-y-3 pt-6 border-t border-gray-200 mb-6" data-id="element-2580">
                  <div className="flex items-center gap-3" data-id="element-2581">
                    <CheckCircleIcon className="h-5 w-5 text-[#007AFF] flex-shrink-0" data-id="element-2582" />
                    <span className="text-sm text-[#6B7280]" data-id="element-2583">
                      Custom design op jouw merk
                    </span>
                  </div>
                  <div className="flex items-center gap-3" data-id="element-2584">
                    <CheckCircleIcon className="h-5 w-5 text-[#007AFF] flex-shrink-0" data-id="element-2585" />
                    <span className="text-sm text-[#6B7280]" data-id="element-2586">
                      Contactformulier en CTA gerichte opbouw
                    </span>
                  </div>
                  <div className="flex items-center gap-3" data-id="element-2587">
                    <CheckCircleIcon className="h-5 w-5 text-[#007AFF] flex-shrink-0" data-id="element-2588" />
                    <span className="text-sm text-[#6B7280]" data-id="element-2589">
                      Klaar voor SEO en uitbreiding
                    </span>
                  </div>
                </div>

                <a href="/prijsscan" className="block w-full bg-[#007AFF] text-white font-semibold py-3 px-6 rounded-lg text-center shadow-md" data-id="element-2590">
                  <span className="flex items-center justify-center" data-id="element-2591">
                    Start de website scan
                    <ArrowRightIcon className="h-5 w-5 ml-2" data-id="element-2592" />
                  </span>
                </a>
                <p className="text-xs text-[#6B7280] text-center mt-4" data-id="element-2593">
                  Duurt 2 minuten, helemaal vrijblijvend
                </p>
              </motion.div>
            </div>
          </div>
        </div>
      </div>

      {/* Website types met prijs */}
      <div id="keuze" className="w-full bg-white py-20 md:py-24" data-id="element-2594">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-2595">
          <div className="max-w-3xl mx-auto text-center mb-12" data-id="element-2596">
            <motion.h2 initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="font-bold text-[#111827] mb-4 text-3xl md:text-4xl" data-id="element-2597">
              Kies het type website dat bij je past
            </motion.h2>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1
          }} className="text-[#6B7280] text-lg leading-relaxed" data-id="element-2598">
              Van een compacte onepager tot een uitgebreide maatwerk website. Drie startpunten, allemaal met dezelfde aandacht voor design en techniek.
            </motion.p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-2599">
            {websiteTypes.map((type, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} whileHover={{
            y: -5,
            borderColor: '#007AFF'
          }} className={`relative rounded-2xl p-8 transition-all duration-300 border-2 group ${type.highlight ? 'bg-white border-[#007AFF] shadow-lg' : 'bg-[#F7F8FA] border-transparent hover:shadow-lg'}`} data-id="element-2600">
                {type.highlight && <div className="absolute -top-3 left-6 inline-flex items-center gap-1 bg-[#007AFF] text-white text-xs font-bold uppercase tracking-wide px-3 py-1 rounded-full shadow-md" data-id="element-2601">
                    <StarIcon className="h-3 w-3" data-id="element-2602" />
                    Populair
                  </div>}
                <motion.div whileHover={{
              scale: 1.1,
              rotate: 5
            }} className="w-14 h-14 bg-[#007AFF] bg-opacity-10 rounded-full flex items-center justify-center text-[#007AFF] mb-4" data-id="element-2603">
                  {type.icon}
                </motion.div>
                <h3 className="font-bold text-[#111827] text-xl mb-1" data-id="element-2604">
                  {type.title}
                </h3>
                <p className="text-[#007AFF] font-semibold text-sm mb-4" data-id="element-2605">
                  {type.scope}
                </p>
                <div className="mb-4 pb-4 border-b border-gray-200" data-id="element-2606">
                  <p className="text-3xl font-bold text-[#111827] mb-1" data-id="element-2607">
                    {type.price}
                  </p>
                  <p className="text-xs text-[#6B7280]" data-id="element-2608">{type.priceSuffix}</p>
                </div>
                <p className="text-[#6B7280] text-sm leading-relaxed mb-6" data-id="element-2609">
                  {type.details}
                </p>
                <a href="/contact" className={`inline-flex items-center justify-center w-full font-semibold py-3 px-4 rounded-lg text-sm transition ${type.highlight ? 'bg-[#007AFF] text-white shadow-md' : 'bg-white text-[#007AFF] border border-[#007AFF]'}`} data-id="element-2610">
                  Vraag dit type aan
                  <ArrowRightIcon className="h-4 w-4 ml-2" data-id="element-2611" />
                </a>
              </motion.div>)}
          </div>

          <div className="max-w-3xl mx-auto mt-10 bg-gradient-to-br from-[#F7F8FA] to-[#E8F0FE] rounded-2xl p-6 text-center" data-id="element-2612">
            <p className="text-[#6B7280] leading-relaxed" data-id="element-2613">
              Twijfel je tussen een onepager en een volwaardige website? Doe de website scan, dan kijken we samen welk type bij jouw doelen past.
            </p>
          </div>
        </div>
      </div>

      {/* Werkwijze timeline */}
      <div id="werkwijze" className="w-full bg-gradient-to-br from-[#F7F8FA] to-[#E8F0FE] py-20 md:py-24" data-id="element-2614">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-2615">
          <div className="max-w-3xl mx-auto text-center mb-16" data-id="element-2616">
            <motion.h2 initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="font-bold text-[#111827] mb-4 text-3xl md:text-4xl" data-id="element-2617">
              Onze werkwijze: duidelijk, persoonlijk en zonder haast
            </motion.h2>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1
          }} className="text-[#6B7280] text-lg leading-relaxed" data-id="element-2618">
              We werken stap voor stap. Ontwerp en afstemmen herhalen we net zo vaak tot je volledig tevreden bent.
            </motion.p>
          </div>

          <div className="relative max-w-4xl mx-auto" data-id="element-2619">
            <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-[#007AFF] bg-opacity-20 hidden md:block" data-id="element-2620">
              <motion.div initial={{
              scaleY: 0
            }} whileInView={{
              scaleY: 1
            }} viewport={{
              once: true
            }} transition={{
              duration: 1.5,
              ease: 'easeInOut'
            }} className="w-full bg-[#007AFF] origin-top" style={{
              height: '100%'
            }} data-id="element-2621" />
            </div>

            <div className="space-y-12" data-id="element-2622">
              {timelineSteps.map((step, index) => <motion.div key={index} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: index * 0.15
            }} className="relative flex items-center gap-8 md:gap-12" data-id="element-2623">
                  <motion.div whileHover={{
                scale: 1.2
              }} className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 bg-[#007AFF] rounded-full items-center justify-center text-white shadow-lg hidden md:flex z-10" data-id="element-2624">
                    {step.icon}
                  </motion.div>
                  <div className={`w-full md:w-5/12 ${index % 2 === 0 ? 'md:ml-auto md:text-left' : 'md:mr-auto md:text-right'}`} data-id="element-2625">
                    <motion.div whileHover={{
                  y: -5,
                  boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)'
                }} className="bg-white rounded-xl p-6 shadow-md transition-all duration-300" data-id="element-2626">
                      <div className="flex items-center gap-3 mb-3 md:hidden" data-id="element-2627">
                        <div className="w-10 h-10 bg-[#007AFF] rounded-full flex items-center justify-center text-white flex-shrink-0" data-id="element-2628">
                          {step.icon}
                        </div>
                        <h3 className="font-bold text-[#111827] text-lg" data-id="element-2629">
                          {step.title}
                        </h3>
                      </div>
                      <h3 className="font-bold text-[#111827] text-lg mb-2 hidden md:block" data-id="element-2630">
                        {step.title}
                      </h3>
                      <p className="text-[#6B7280] leading-relaxed" data-id="element-2631">
                        {step.description}
                      </p>
                    </motion.div>
                  </div>
                </motion.div>)}
            </div>
          </div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.5
        }} className="text-center mt-12" data-id="element-2632">
            <motion.a href="/hosting" whileHover={{
            x: 5
          }} className="inline-flex items-center text-[#007AFF] font-medium" data-id="element-2633">
              Bekijk onze hosting (€34,99 per maand)
              <ArrowRightIcon className="h-4 w-4 ml-2" data-id="element-2634" />
            </motion.a>
          </motion.div>
        </div>
      </div>

      {/* Waarom maatwerk */}
      <div className="w-full bg-white py-20 md:py-24" data-id="element-2635">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-2636">
          <div className="max-w-3xl mx-auto text-center mb-12" data-id="element-2637">
            <motion.h2 initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="font-bold text-[#111827] mb-4 text-3xl md:text-4xl" data-id="element-2638">
              Waarom een maatwerk website?
            </motion.h2>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1
          }} className="text-[#6B7280] text-lg leading-relaxed" data-id="element-2639">
              Een maatwerk website biedt de flexibiliteit en professionaliteit die jouw bedrijf nodig heeft. Geen template, wel een verhaal dat past bij jouw markt.
            </motion.p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12" data-id="element-2640">
            {benefits.map((benefit, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} whileHover={{
            y: -5,
            boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)'
          }} className="bg-[#F7F8FA] rounded-2xl p-6 transition-all duration-300 group" data-id="element-2641">
                <motion.div whileHover={{
              scale: 1.1,
              rotate: 5
            }} className="w-12 h-12 bg-[#007AFF] bg-opacity-10 rounded-full flex items-center justify-center text-[#007AFF] mb-4" data-id="element-2642">
                  {benefit.icon}
                </motion.div>
                <h3 className="font-bold text-[#111827] text-lg mb-2" data-id="element-2643">
                  {benefit.title}
                </h3>
                <p className="text-[#6B7280] leading-relaxed text-sm" data-id="element-2644">
                  {benefit.description}
                </p>
              </motion.div>)}
          </div>

          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} className="bg-gradient-to-br from-[#F7F8FA] to-[#E8F0FE] rounded-2xl p-8 max-w-3xl mx-auto" data-id="element-2645">
            <h3 className="font-bold text-[#111827] text-xl mb-4 text-center" data-id="element-2646">
              Compleet pakket bij één partij
            </h3>
            <p className="text-[#6B7280] text-center mb-6" data-id="element-2647">
              Een professionele website is meer dan alleen design. Combineer met hosting, e-mail en SEO.
            </p>
            <div className="flex flex-wrap gap-4 justify-center" data-id="element-2648">
              <motion.a href="/hosting" whileHover={{
              scale: 1.05
            }} whileTap={{
              scale: 0.95
            }} className="inline-flex items-center px-6 py-3 bg-white text-[#007AFF] font-semibold rounded-lg shadow-sm group" data-id="element-2649">
                Hosting voor €34,99 per maand
                <ArrowRightIcon className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-2650" />
              </motion.a>
              <motion.a href="/email" whileHover={{
              scale: 1.05
            }} whileTap={{
              scale: 0.95
            }} className="inline-flex items-center px-6 py-3 bg-white text-[#007AFF] font-semibold rounded-lg shadow-sm group" data-id="element-2651">
                Zakelijke e-mail vanaf €1,99
                <ArrowRightIcon className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-2652" />
              </motion.a>
              <motion.a href="/seo" whileHover={{
              scale: 1.05
            }} whileTap={{
              scale: 0.95
            }} className="inline-flex items-center px-6 py-3 bg-white text-[#007AFF] font-semibold rounded-lg shadow-sm group" data-id="element-2653">
                SEO optimalisatie
                <ArrowRightIcon className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-2654" />
              </motion.a>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Norden begeleiding */}
      <div className="w-full bg-gradient-to-br from-[#007AFF] to-[#005FCC] py-20 md:py-24" data-id="element-2655">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-2656">
          <div className="max-w-4xl mx-auto" data-id="element-2657">
            <div className="bg-white rounded-2xl p-8 md:p-12 shadow-xl" data-id="element-2658">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center" data-id="element-2659">
                <div className="text-center md:text-left" data-id="element-2660">
                  <div className="inline-block relative" data-id="element-2661">
                    <motion.div initial={{
                    opacity: 0,
                    scale: 0.8
                  }} whileInView={{
                    opacity: 1,
                    scale: 1
                  }} viewport={{
                    once: true
                  }} transition={{
                    type: 'spring',
                    stiffness: 100
                  }} className="w-48 h-48 bg-gradient-to-br from-[#007AFF] to-[#005FCC] rounded-full flex items-center justify-center p-4 relative overflow-hidden" data-id="element-2662">
                      <img src="https://cdn.magicpatterns.com/uploads/fv5nFyNt1SPvWUkxd2YKYJ/Norden_portret_300dpi_2.png" alt="Norden begeleidt jouw website traject" className="w-full h-full object-cover" data-id="element-2663" />
                    </motion.div>
                    <motion.div animate={{
                    y: [0, -5, 0],
                    rotate: [0, 5, 0]
                  }} transition={{
                    duration: 3,
                    repeat: Infinity,
                    delay: 0
                  }} className="absolute -top-2 -right-2 w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-lg" data-id="element-2664">
                      <CheckCircleIcon className="h-6 w-6 text-[#007AFF]" data-id="element-2665" />
                    </motion.div>
                    <motion.div animate={{
                    y: [0, -5, 0],
                    rotate: [0, -5, 0]
                  }} transition={{
                    duration: 4,
                    repeat: Infinity,
                    delay: 1
                  }} className="absolute -bottom-2 -left-2 w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-lg" data-id="element-2666">
                      <UsersIcon className="h-6 w-6 text-[#007AFF]" data-id="element-2667" />
                    </motion.div>
                  </div>
                </div>
                <div data-id="element-2668">
                  <motion.h2 initial={{
                  opacity: 0,
                  x: 20
                }} whileInView={{
                  opacity: 1,
                  x: 0
                }} viewport={{
                  once: true
                }} className="font-bold text-[#111827] text-3xl mb-4" data-id="element-2669">
                    Norden begeleidt jouw traject
                  </motion.h2>
                  <motion.p initial={{
                  opacity: 0,
                  x: 20
                }} whileInView={{
                  opacity: 1,
                  x: 0
                }} viewport={{
                  once: true
                }} transition={{
                  delay: 0.1
                }} className="text-[#6B7280] text-lg leading-relaxed mb-6" data-id="element-2670">
                    Onze mascotte Norden staat symbool voor hoe wij werken. Als team, samen met jou, naar het gewenste resultaat. Van eerste gesprek tot oplevering staan wij naast je.
                  </motion.p>
                  <div className="space-y-3" data-id="element-2671">
                    <div className="flex items-center gap-3" data-id="element-2672">
                      <div className="w-6 h-6 bg-[#007AFF] rounded-full flex items-center justify-center flex-shrink-0" data-id="element-2673">
                        <CheckIcon className="h-4 w-4 text-white" data-id="element-2674" />
                      </div>
                      <span className="text-[#6B7280]" data-id="element-2675">
                        Persoonlijke begeleiding door het hele traject
                      </span>
                    </div>
                    <div className="flex items-center gap-3" data-id="element-2676">
                      <div className="w-6 h-6 bg-[#007AFF] rounded-full flex items-center justify-center flex-shrink-0" data-id="element-2677">
                        <CheckIcon className="h-4 w-4 text-white" data-id="element-2678" />
                      </div>
                      <span className="text-[#6B7280]" data-id="element-2679">
                        Directe communicatie met het ontwikkelteam
                      </span>
                    </div>
                    <div className="flex items-center gap-3" data-id="element-2680">
                      <div className="w-6 h-6 bg-[#007AFF] rounded-full flex items-center justify-center flex-shrink-0" data-id="element-2681">
                        <CheckIcon className="h-4 w-4 text-white" data-id="element-2682" />
                      </div>
                      <span className="text-[#6B7280]" data-id="element-2683">
                        Doorwerken tot je volledig tevreden bent
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Cases */}
      <div className="w-full bg-[#F7F8FA] py-20 md:py-24" data-id="element-2684">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-2685">
          <div className="max-w-3xl mx-auto text-center mb-12" data-id="element-2686">
            <motion.h2 initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="font-bold text-[#111827] mb-4 text-3xl md:text-4xl" data-id="element-2687">
              Recent opgeleverd
            </motion.h2>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1
          }} className="text-[#6B7280] text-lg leading-relaxed" data-id="element-2688">
              Een paar van de laatste websites die we hebben opgeleverd. Bekijk de complete portfolio voor meer voorbeelden.
            </motion.p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12" data-id="element-2689">
            {topCases.map((clientCase, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} whileHover={{
            y: -10
          }} className="bg-white rounded-2xl overflow-hidden shadow-md transition-all duration-300 group" data-id="element-2690">
                <div className="relative h-48 overflow-hidden" data-id="element-2691">
                  <motion.img whileHover={{
                scale: 1.1
              }} transition={{
                duration: 0.5
              }} src={clientCase.image} alt={`${clientCase.name} website door Noord Digital`} className="w-full h-full object-cover" data-id="element-2692" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" data-id="element-2693"></div>
                  <div className="absolute bottom-4 left-4 right-4" data-id="element-2694">
                    <span className="inline-block bg-[#007AFF] text-white text-xs font-medium px-3 py-1 rounded-full mb-2" data-id="element-2695">
                      {clientCase.sector}
                    </span>
                    <h3 className="text-white font-bold text-lg" data-id="element-2696">
                      {clientCase.name}
                    </h3>
                  </div>
                </div>
              </motion.div>)}
          </div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.4
        }} className="text-center" data-id="element-2697">
            <motion.a href="/portfolio" whileHover={{
            scale: 1.05
          }} whileTap={{
            scale: 0.95
          }} className="inline-flex items-center bg-[#007AFF] text-white font-semibold py-3 px-8 rounded-lg shadow-md group" data-id="element-2698">
              Bekijk alle cases
              <ArrowRightIcon className="h-5 w-5 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-2699" />
            </motion.a>
          </motion.div>
        </div>
      </div>

      <Footer data-id="element-2700" />
    </div>;
};